import React, { useMemo, useState } from "react";
import {
    Button,
    CircularProgress,
    Menu,
    MenuItem,
    Typography,
} from "@mui/material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import CatalogPageShell from "../components/Products/CatalogPageShell.jsx";
import ReturnsGrid from "../components/Shipping/ReturnsGrid.jsx";
import PullDateRangeFields from "../components/Helpers/PullDateRangeFields.jsx";
import useApiData from "../services/useApiData.js";
import PostData from "../services/PostData.js";
import { catalogQuietButtonSx } from "../components/Products/catalogGridStyles.js";
import "../components/Products/catalog.css";

function dayInputValue(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function defaultPullRange() {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 14);
    return { startDate: dayInputValue(start), endDate: dayInputValue(end) };
}

function pullRangeError(startDate, endDate) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
        return "Choose a start and end date.";
    }
    if (startDate > endDate) return "Start date must be on or before the end date.";
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    const days = Math.round((end.getTime() - start.getTime()) / 86400000);
    if (days > 62) return "Date range cannot be longer than 62 days.";
    return "";
}

function summarizePull(result) {
    const wm = result?.walmart || {};
    const eb = result?.ebay || {};
    const created = (wm.created || 0) + (eb.created || 0);
    const updated = (wm.updated || 0) + (eb.updated || 0);
    const fetched = (wm.fetched || 0) + (eb.fetched || 0);
    const errors = [...(wm.errors || []), ...(eb.errors || [])];
    return { created, updated, fetched, errors };
}

export default function Returns() {
    const { data, error, isLoading, load, refetch } = useApiData("api/broylesllc/returns");
    const returns = Array.isArray(data) ? data : [];

    const initialRange = useMemo(() => defaultPullRange(), []);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [pulling, setPulling] = useState(false);
    const [pullError, setPullError] = useState("");
    const [pullHint, setPullHint] = useState("");
    const [startDate, setStartDate] = useState(initialRange.startDate);
    const [endDate, setEndDate] = useState(initialRange.endDate);
    const rangeError = pullRangeError(startDate, endDate);

    const pullReturns = async (action) => {
        if (rangeError) return;
        setMenuAnchor(null);
        setPulling(true);
        setPullError("");
        setPullHint("");
        try {
            const response = await PostData(
                { action, startDate, endDate },
                "broylesllc/returns/pull",
                null
            );
            if (!response?.success) {
                const message = response?.error?.response?.data?.message;
                setPullError(message || "Could not pull returns for that date range.");
                return;
            }
            const summary = summarizePull(response.data);
            if (summary.errors.length && summary.created + summary.updated === 0) {
                setPullError(
                    summary.errors[0]?.message || "Could not pull returns for that date range."
                );
            } else {
                setPullHint(
                    `Pulled ${summary.fetched}: ${summary.created} new, ${summary.updated} updated.`
                );
                if (summary.errors.length) {
                    setPullError(
                        `${summary.errors.length} channel warning(s). ${summary.errors[0]?.message || ""}`
                    );
                }
            }
            await refetch?.();
        } finally {
            setPulling(false);
        }
    };

    return (
        <CatalogPageShell
            title="Returns"
            subtitle="Claims and return tracking — pull from Walmart and eBay, then manage status here."
        >
            {error ? (
                <p style={{ color: "#b91c1c", padding: "8px 16px", margin: 0 }}>
                    Failed to load returns.
                </p>
            ) : null}
            <div className="app-page-stack">
                <div className="catalog-toolbar" style={{ borderBottom: "none" }}>
                    <div className="catalog-toolbar-spacer" />
                    {pullError ? (
                        <Typography component="span" sx={{ color: "#b91c1c", fontSize: 13 }}>
                            {pullError}
                        </Typography>
                    ) : pullHint ? (
                        <Typography component="span" sx={{ color: "#64748b", fontSize: 13 }}>
                            {pullHint}
                        </Typography>
                    ) : (
                        <span className="catalog-selection-hint">
                            Sync marketplace returns into this list
                        </span>
                    )}
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={pulling}
                        startIcon={
                            pulling ? (
                                <CircularProgress size={14} />
                            ) : (
                                <SyncOutlinedIcon fontSize="small" />
                            )
                        }
                        endIcon={<KeyboardArrowDownOutlinedIcon fontSize="small" />}
                        onClick={(e) => setMenuAnchor(e.currentTarget)}
                        sx={catalogQuietButtonSx}
                    >
                        Pull returns
                    </Button>
                    <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={() => setMenuAnchor(null)}
                        slotProps={{ paper: { sx: { minWidth: 300, px: 0.5, py: 0.5 } } }}
                    >
                        <PullDateRangeFields
                            startDate={startDate}
                            endDate={endDate}
                            onStartChange={setStartDate}
                            onEndChange={setEndDate}
                            error={rangeError}
                            hint="Upserts RMA / Return ID; keeps claim filing fields."
                        />
                        <MenuItem
                            disabled={Boolean(rangeError) || pulling}
                            onClick={() => pullReturns("all")}
                        >
                            All channels
                        </MenuItem>
                        <MenuItem
                            disabled={Boolean(rangeError) || pulling}
                            onClick={() => pullReturns("ebay")}
                        >
                            eBay
                        </MenuItem>
                        <MenuItem
                            disabled={Boolean(rangeError) || pulling}
                            onClick={() => pullReturns("walmart")}
                        >
                            Walmart
                        </MenuItem>
                    </Menu>
                </div>
                <ReturnsGrid
                    returns={returns}
                    loading={Boolean(load || isLoading || pulling)}
                    onSaved={refetch}
                />
            </div>
        </CatalogPageShell>
    );
}
